/**
 * P&L Reconciliation Grid
 * =======================
 * Year-at-a-glance table showing all P&L line items with reconciliation status.
 *
 * Features:
 * - Multiple years display
 * - Status badges (✓ Reconciled, ⚠️ In Review, ○ Not Started)
 * - Click cell to drill down into transactions
 * - Catch-all item highlighting (needs review)
 */

import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Circle } from 'lucide-react';

interface PLReconciliationGridProps {
  plGridData: {
    report_id: string;
    years: number[];
    pl_structure: any;
    pl_grid: any;
    reconciliation_status: Record<string, string>;
    catch_all_items: Array<[string, string]>;
  };
  onCellClick: (year: number, month: number, section: string, lineItem: string) => void;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrency = (value: number): string => {
  if (value === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'reconciled':
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 font-medium">
          <CheckCircle size={12} />
          Reconciled
        </span>
      );
    case 'in_review':
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
          <AlertCircle size={12} />
          In Review
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
          <Circle size={12} />
          Not Started
        </span>
      );
  }
};

export default function PLReconciliationGrid({ plGridData, onCellClick }: PLReconciliationGridProps) {
  const [selectedYear, setSelectedYear] = useState<number>(plGridData.years[plGridData.years.length - 1] || 2023);

  const { pl_structure, pl_grid, reconciliation_status, catch_all_items } = plGridData;

  // Check if a line item is a catch-all
  const isCatchAll = (section: string, lineItem: string): boolean => {
    return catch_all_items.some(([s, li]) => s === section && li === lineItem);
  };

  // Get reconciliation status for a cell
  const getCellStatus = (year: number, month: number, lineItem: string): string => {
    const cellId = `${year}-${String(month).padStart(2, '0')}-${lineItem.replace(/ /g, '_')}`;
    return reconciliation_status[cellId] || 'not_started';
  };

  // Get cell CSS classes
  const getCellClasses = (section: string, lineItem: string, value: number): string => {
    const baseClasses = 'px-3 py-2 text-right text-sm cursor-pointer transition-colors hover:bg-blue-50';
    const catchAllClasses = isCatchAll(section, lineItem) ? 'bg-yellow-50' : '';
    const valueClasses = value === 0 ? 'text-gray-400' : 'text-gray-900 font-medium';

    return `${baseClasses} ${catchAllClasses} ${valueClasses}`.trim();
  };

  if (!pl_grid[selectedYear] && !pl_grid[String(selectedYear)]) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">No data available for {selectedYear}</p>
      </div>
    );
  }

  // Handle both string and number keys in pl_grid
  const yearData = pl_grid[selectedYear] || pl_grid[String(selectedYear)];

  if (!yearData || typeof yearData !== 'object') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">No P&L data available for {selectedYear}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Year-at-a-Glance P&L Reconciliation</h2>
          <div className="flex gap-2">
            {plGridData.years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedYear === year
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 bg-yellow-50 border border-yellow-200 rounded"></div>
            = Catch-All (Needs Review)
          </span>
          <StatusBadge status="reconciled" />
          <StatusBadge status="in_review" />
          <StatusBadge status="not_started" />
        </div>
      </div>

      {/* P&L Grid */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                  Category
                </th>
                {MONTH_NAMES.map((month, idx) => (
                  <th key={month} className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {month}
                  </th>
                ))}
                <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider bg-gray-100">
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {/* Render each section */}
              {Object.entries(yearData).map(([section, lineItems]: [string, any]) => (
                <React.Fragment key={section}>
                  {/* Section Header */}
                  <tr className="bg-blue-50 border-t-2 border-blue-200">
                    <td
                      colSpan={14}
                      className="px-4 py-3 text-sm font-bold text-blue-900 uppercase tracking-wide sticky left-0 bg-blue-50"
                    >
                      {section}
                    </td>
                  </tr>

                  {/* Line Items */}
                  {Object.entries(lineItems).map(([lineItem, monthlyValues]: [string, any]) => {
                    const values = Array.isArray(monthlyValues) ? monthlyValues : [];
                    const total = values.reduce((sum, val) => sum + (val || 0), 0);

                    return (
                      <tr key={`${section}-${lineItem}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 sticky left-0 bg-white font-medium whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {lineItem}
                            {isCatchAll(section, lineItem) && (
                              <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium">
                                ⚠️ Review
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Monthly Values */}
                        {values.map((value: number, monthIndex: number) => {
                          const status = getCellStatus(selectedYear, monthIndex + 1, lineItem);
                          return (
                            <td
                              key={monthIndex}
                              className={getCellClasses(section, lineItem, value)}
                              onClick={() => value !== 0 && onCellClick(selectedYear, monthIndex + 1, section, lineItem)}
                              title={`Click to reconcile ${lineItem} for ${MONTH_NAMES[monthIndex]} ${selectedYear}`}
                            >
                              <div className="flex flex-col items-end gap-1">
                                <span>{formatCurrency(value)}</span>
                                {value !== 0 && status !== 'not_started' && (
                                  <div className="scale-75 origin-right">
                                    <StatusBadge status={status} />
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}

                        {/* Total */}
                        <td className="px-3 py-3 text-right text-sm font-bold text-gray-900 bg-gray-50">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Section Total Row */}
                  {section !== 'OPERATING EXPENSES' && (
                    <tr className="bg-gray-100 border-t border-gray-300 font-bold">
                      <td className="px-4 py-3 text-sm text-gray-900 sticky left-0 bg-gray-100">
                        Total {section}
                      </td>
                      {MONTH_NAMES.map((_, monthIndex) => {
                        const monthTotal = Object.values(lineItems).reduce(
                          (sum: number, monthlyValues: any) => sum + (monthlyValues[monthIndex] || 0),
                          0
                        );
                        return (
                          <td key={monthIndex} className="px-3 py-3 text-right text-sm text-gray-900">
                            {formatCurrency(monthTotal)}
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-right text-sm text-gray-900 bg-gray-200">
                        {formatCurrency(
                          Object.values(lineItems).reduce(
                            (sum: number, monthlyValues: any) =>
                              sum + monthlyValues.reduce((s: number, v: number) => s + (v || 0), 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How to Use:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Click any cell</strong> with a dollar amount to drill down into transactions</li>
          <li>• <strong>Yellow highlighted rows</strong> are catch-all categories (e.g., "Other Operating Expenses") that may contain misclassified transactions</li>
          <li>• <strong>Status badges</strong> show reconciliation progress (Not Started → In Review → Reconciled)</li>
          <li>• Review and sign off each cell to mark as "Reconciled"</li>
        </ul>
      </div>
    </div>
  );
}
